import React, { useEffect, useRef, useState } from 'react';
import { useAppState } from '../../context/AppStateContext';
import {
  OrganisationDocument,
  OrganisationDocumentDataService,
  OrganisationDocumentMetadata,
} from '../../services/organisationDocumentDataService';
import HelpAsk from '../../help/HelpAsk';
import ConfirmationModal from '../ConfirmationModal';
import PdfViewer from '../common/PdfViewer';
import './documents.scss';

const fmtDate = (d: string | null | undefined): string =>
  d ? new Date(d).toLocaleDateString() : '—';

const fmtDateTime = (d: string | null | undefined): string =>
  d
    ? `${new Date(d).toLocaleDateString()}, ${new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : '—';

const stripPdfExt = (name: string): string => name.replace(/\.pdf$/i, '');

const OrganisationDocuments: React.FC = () => {
  const { userRoleIds, roles } = useAppState();

  const isAdmin = userRoleIds.includes(roles.Admin);
  // Organisation documents (e.g. policies) are downloadable by anyone who can
  // view them — parents included.
  const canDownload = true;

  const [docs, setDocs] = useState<OrganisationDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [viewerDoc, setViewerDoc] = useState<OrganisationDocument | null>(null);
  const [editDoc, setEditDoc] = useState<OrganisationDocument | null>(null);
  const [deleteDoc, setDeleteDoc] = useState<OrganisationDocument | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [search, setSearch] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setDocs(await OrganisationDocumentDataService.list());
    } catch (err) {
      console.error('Failed to load organisation documents', err);
      setError('Failed to load documents.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    const list = Array.from(files);
    const pdfs = list.filter((f) => f.type === 'application/pdf');
    if (pdfs.length < list.length) {
      setError('Only PDF files can be uploaded.');
      if (pdfs.length === 0) return;
    }
    setUploading(true);
    setError(null);
    try {
      for (const file of pdfs) {
        await OrganisationDocumentDataService.upload(file, stripPdfExt(file.name));
      }
      await load();
    } catch (err) {
      console.error('Upload failed', err);
      setError('One or more uploads failed.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (doc: OrganisationDocument) => {
    try {
      const url = await OrganisationDocumentDataService.getDownloadUrl(doc.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Download failed', err);
      setError('Failed to download document.');
    }
  };

  // Organisation documents are listed alphabetically by name (case-insensitive),
  // optionally filtered by the name search.
  const searchTerm = search.trim().toLowerCase();
  const sortedDocs = [...docs]
    .filter((d) => searchTerm === '' || d.display_name.toLowerCase().includes(searchTerm))
    .sort((a, b) => a.display_name.localeCompare(b.display_name, undefined, { sensitivity: 'base' }));

  const handleDeleteConfirm = async () => {
    if (!deleteDoc) return;
    try {
      await OrganisationDocumentDataService.remove(deleteDoc.id);
      setDeleteDoc(null);
      await load();
    } catch (err) {
      console.error('Delete failed', err);
      setError('Failed to delete document.');
      setDeleteDoc(null);
    }
  };

  return (
    <div className="child-documents">
      <div className="cd-head">
        <h3 className="cd-head__title">Organisation documents</h3>
        {docs.length > 0 && (
          <span className="cd-head__count">{docs.length} {docs.length === 1 ? 'document' : 'documents'}</span>
        )}
      </div>

      {/* Upload panel (Admin only) */}
      {isAdmin && (
        <div className="cd-upload-panel">
          <div className="cd-upload-panel__head">
            <span className="cd-upload-panel__title">Add a document</span>
            <span className="cd-upload-panel__hint">(PDF only)</span>
          </div>
          <div className="cd-upload-panel__row">
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => handleFiles(e.target.files)}
            />
            <div
              className={`cd-dropzone${isDragging ? ' is-dragging' : ''}${uploading ? ' is-busy' : ''}`}
              data-help="documents.dropzone"
              onClick={() => !uploading && fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); if (!uploading) setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                if (!uploading) handleFiles(e.dataTransfer.files);
              }}
            >
              {uploading ? (
                <><span className="spinner-border spinner-border-sm" /><span>Uploading…</span></>
              ) : (
                <>
                  <i className="fas fa-cloud-arrow-up" />
                  <span>Drag a PDF here, or</span>
                  <span className="cd-btn cd-btn--primary"><i className="fas fa-file-lines" />Choose PDF</span>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {error && <div className="cd-alert"><i className="fas fa-triangle-exclamation" />{error}</div>}

      {(loading || docs.length > 0) && (
        <div className="cd-filter">
          <div className="cd-search">
            <i className="fas fa-search" />
            <input
              type="text"
              placeholder="Search by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      )}

      {loading ? (
        <div className="cd-empty"><div className="loading-spinner" />Loading documents…</div>
      ) : docs.length === 0 ? (
        <div className="cd-empty">
          <i className="fas fa-folder-open cd-empty__icon" />
          <div>
            No organisation documents yet.
            <div className="mt-3">
              <HelpAsk guideKey="documents.open-document" label="How do documents work?" variant="inline" />
            </div>
            {/* An empty list is where an Admin most needs the upload guide, and the
                plain sentence it replaces could only point at the panel. */}
            {isAdmin && (
              <div className="mt-1">
                <HelpAsk guideKey="documents.add-document" label="How do I add one?" variant="inline" />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="cd-table-wrap">
          <table className="cd-table">
            <thead>
              <tr>
                <th className="cd-th">Name</th>
                <th className="cd-th">Document date</th>
                <th className="cd-th">Uploaded</th>
                <th className="cd-th">By</th>
                <th className="cd-th cd-th--right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedDocs.map((doc) => (
                <tr key={doc.id} className="cd-row">
                  <td className="cd-td">
                    <button type="button" className="cd-name" onClick={() => setViewerDoc(doc)} title="View document">
                      <i className="fas fa-file-lines" />
                      <span className="cd-name__text">{doc.display_name}</span>
                    </button>
                  </td>
                  <td className="cd-td cd-td--muted">{fmtDate(doc.document_date)}</td>
                  <td className="cd-td cd-td--muted">{fmtDateTime(doc.uploaded_at)}</td>
                  <td className="cd-td">{doc.created_by_name ?? '—'}</td>
                  <td className="cd-td cd-td--right">
                    <span className="cd-actions">
                      <button type="button" className="cd-action cd-action--view" onClick={() => setViewerDoc(doc)} title="View">
                        <i className="fas fa-eye" />
                      </button>
                      {canDownload && (
                        <button type="button" className="cd-action cd-action--download" onClick={() => handleDownload(doc)} title="Download">
                          <i className="fas fa-download" />
                        </button>
                      )}
                      {isAdmin && (
                        <>
                          <button type="button" className="cd-action cd-action--edit" onClick={() => setEditDoc(doc)} title="Edit details">
                            <i className="fas fa-pen" />
                          </button>
                          <button type="button" className="cd-action cd-action--delete" onClick={() => setDeleteDoc(doc)} title="Delete">
                            <i className="fas fa-trash" />
                          </button>
                        </>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
              {sortedDocs.length === 0 && (
                <tr>
                  <td className="cd-td cd-td--muted" colSpan={5} style={{ textAlign: 'center', padding: '28px' }}>
                    No documents match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {viewerDoc && (
        <PdfViewer
          sourceKey={viewerDoc.id}
          title={viewerDoc.display_name}
          load={async () => ({ url: await OrganisationDocumentDataService.getViewUrl(viewerDoc.id) })}
          onClose={() => setViewerDoc(null)}
        />
      )}

      {editDoc && (
        <EditOrgDocumentModal
          doc={editDoc}
          onClose={() => setEditDoc(null)}
          onSaved={async () => { setEditDoc(null); await load(); }}
        />
      )}

      <ConfirmationModal
        show={!!deleteDoc}
        title="Delete document"
        message={`Delete "${deleteDoc?.display_name ?? ''}"? This cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDoc(null)}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

// --- Edit metadata modal (Admin only) -------------------------------------

const EditOrgDocumentModal: React.FC<{
  doc: OrganisationDocument;
  onClose: () => void;
  onSaved: () => void;
}> = ({ doc, onClose, onSaved }) => {
  const [displayName, setDisplayName] = useState(doc.display_name);
  const [description, setDescription] = useState(doc.description ?? '');
  const [documentDate, setDocumentDate] = useState(doc.document_date ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!displayName.trim()) {
      setErr('Name is required.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const payload: OrganisationDocumentMetadata = {
        display_name: displayName.trim(),
        description: description.trim() || null,
        document_date: documentDate || null,
      };
      await OrganisationDocumentDataService.update(doc.id, payload);
      onSaved();
    } catch (e) {
      console.error('Failed to save document', e);
      setErr('Failed to save changes.');
      setSaving(false);
    }
  };

  return (
    <div className="cd-modal-backdrop" onClick={onClose}>
      <div className="cd-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cd-modal__head">
          <div className="cd-modal__heading">
            <span className="cd-modal__eyebrow">Edit document</span>
            <h3 className="cd-modal__title" title={doc.display_name}>{doc.display_name}</h3>
          </div>
          <button type="button" className="cd-modal__close" onClick={onClose} title="Close" aria-label="Close">
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="cd-modal__body">
          {err && <div className="cd-alert"><i className="fas fa-triangle-exclamation" />{err}</div>}

          <div className="cd-form-row">
            <label className="cd-form-label">Name</label>
            <input className="cd-input" value={displayName} autoFocus onChange={(e) => setDisplayName(e.target.value)} />
          </div>

          <div className="cd-form-row">
            <label className="cd-form-label">Document date</label>
            <input type="date" className="cd-input" value={documentDate} onChange={(e) => setDocumentDate(e.target.value)} />
          </div>

          <div className="cd-form-row">
            <label className="cd-form-label">Description</label>
            <textarea className="cd-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
        </div>

        <div className="cd-modal__foot">
          <button type="button" className="cd-btn cd-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="cd-btn cd-btn--primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrganisationDocuments;
